import React, { CSSProperties, useEffect, useMemo, useState } from "react";

import { Button, Icon, Modal, Radio } from "semantic-ui-react";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import formatComma from "react-lib/utils/formatComma";

import {
  OptimizePackagePlanType,
  OptimizePackageType,
  getOptimizePackagePlan,
} from "./sequence/PackagePurchase";

type ModOptimizePackageProps = {
  setProp: (key: string, value: any, callback?: () => any) => any;
  data?: OptimizePackageType | null;
  buttonLoadCheck?: Record<string, any>;
  paramKey: string;
  onClose: () => void;
  onConfirm: (plan: OptimizePackagePlanType) => void;
};

type OptionType = "PACKAGE" | "NONE";

const STYLES: Record<string, CSSProperties> = {
  summary: {
    background: "#F4F7F9",
    border: "1px solid #E3E9ED",
    borderRadius: "6px",
    padding: "1rem",
  },
  chip: {
    border: "1px solid #D5DDE2",
    borderRadius: "20px",
    background: "white",
    padding: "0.25rem 0.75rem",
    fontSize: "0.85rem",
  },
  option: {
    border: "1px solid #D5DDE2",
    borderRadius: "6px",
    padding: "1rem",
    cursor: "pointer",
  },
  optionActive: {
    borderColor: "var(--primary-theme-color)",
    background: "color-mix(in srgb, var(--primary-theme-color) 6%, white)",
  },
  badge: {
    background: "#1E8E4E",
    color: "white",
    borderRadius: "20px",
    padding: "0.25rem 0.75rem",
    fontWeight: "bold",
    whiteSpace: "nowrap",
  },
  extra: {
    background: "#FFF8E6",
    border: "1px solid #F2C66D",
    borderRadius: "6px",
    padding: "0.75rem",
    marginTop: "0.75rem",
  },
  muted: { color: "#8A959C" },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" },
};

const ModOptimizePackage = (props: ModOptimizePackageProps) => {
  const [option, setOption] = useState<OptionType>("NONE");
  const [showItems, setShowItems] = useState(false);

  const orders = props.data?.orders || [];

  const plan = useMemo(
    () => getOptimizePackagePlan(orders, props.data?.packages || []),
    [props.data],
  );

  const { packages } = plan;

  const isRecommended = packages.length > 0 && plan.coveredOrders.length > 0 && plan.savings > 0;

  const selectedCodes = useMemo(
    () => new Set(orders.flatMap((order) => (order.items || []).map((item) => item.product_code))),
    [props.data],
  );

  const packageItems = packages.flatMap((selection) =>
    (selection.package.items || []).filter((item) => item.active !== false),
  );

  useEffect(() => {
    setOption(isRecommended ? "PACKAGE" : "NONE");
    setShowItems(false);
  }, [props.data]);

  const handleConfirm = () => {
    if (option === "PACKAGE") {
      props.onConfirm(plan);
    } else {
      props.onClose();
    }
  };

  return (
    <Modal open={!!props.data} size="small" closeOnDimmerClick={false} onClose={props.onClose}>
      <Modal.Header style={STYLES.row}>
        คำนวณแพ็กเกจที่คุ้มกว่า
        <Icon name="close" link style={{ fontSize: "1rem" }} onClick={props.onClose} />
      </Modal.Header>

      <Modal.Content scrolling>
        <div style={STYLES.summary}>
          <div style={{ ...STYLES.row, fontWeight: "bold", marginBottom: "0.5rem" }}>
            <span>{orders.length} รายการที่เลือก</span>
            <span>{formatComma(plan.selectedTotal)}</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {orders.map((order) => (
              <span key={order.id} style={STYLES.chip}>
                {order.product_name} · {formatComma(order.price)}
              </span>
            ))}
          </div>
        </div>

        <div style={{ fontWeight: "bold", margin: "1.25rem 0 0.75rem" }}>แพ็กเกจที่แนะนำ</div>

        {isRecommended ? (
          <div
            style={{ ...STYLES.option, ...(option === "PACKAGE" && STYLES.optionActive) }}
            onClick={() => setOption("PACKAGE")}
          >
            <div style={STYLES.row}>
              <Radio
                checked={option === "PACKAGE"}
                label={packages
                  .map(({ package: item }) =>
                    item.name_en && item.name_en !== item.name
                      ? `${item.name_en} (${item.name})`
                      : item.name,
                  )
                  .join(" + ")}
                style={{ fontWeight: "bold" }}
              />
              <span style={STYLES.badge}>ประหยัด {formatComma(plan.savings)} บาท</span>
            </div>

            <div style={{ paddingLeft: "1.75rem", marginTop: "0.5rem" }}>
              {packages.map((selection) => (
                <div key={selection.package.id} style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
                  <span style={STYLES.muted}>{selection.package.code}</span>
                  <span style={STYLES.muted}>ราคาแพ็กเกจ</span>
                  <span style={{ fontSize: "1.3rem", fontWeight: "bold", color: "var(--primary-theme-color)" }}>
                    {formatComma((Number(selection.total_price) || 0) / (Number(selection.quantity) || 1))}
                  </span>
                  {Number(selection.quantity) > 1 && <span style={STYLES.muted}>x {selection.quantity}</span>}
                </div>
              ))}

              <div style={{ marginTop: "0.5rem" }}>
                ครอบคลุม {plan.coveredOrders.length} จาก {orders.length} รายการ:{" "}
                {plan.coveredOrders.map((order) => order.product_name).join(" · ")}
              </div>

              <div
                style={{ color: "var(--primary-theme-color)", fontWeight: "bold", margin: "0.5rem 0", cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowItems(!showItems);
                }}
              >
                <Icon name={showItems ? "chevron up" : "chevron down"} size="small" />
                ดูรายการตรวจในแพ็กเกจทั้งหมด ({packageItems.length})
              </div>

              {showItems && (
                <div style={{ paddingLeft: "1.25rem", marginBottom: "0.5rem" }}>
                  {packageItems.map((item, index) => (
                    <div key={`${item.product_code}-${index}`} style={STYLES.row}>
                      <span>{item.product_name}</span>
                      {selectedCodes.has(item.product_code) && (
                        <span style={{ color: "#1E8E4E", whiteSpace: "nowrap" }}>อยู่ในรายการที่เลือก</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {plan.extraOrders.length > 0 ? (
                <div style={STYLES.extra}>
                  <div style={{ color: "#B37200", fontWeight: "bold", marginBottom: "0.25rem" }}>
                    รายการนอกแพ็กเกจ ต้องคิดแยกเพิ่ม {plan.extraOrders.length} รายการ
                  </div>
                  {plan.extraOrders.map(({ order, price }) => (
                    <div key={order.id} style={STYLES.row}>
                      <span style={{ fontWeight: "bold" }}>{order.product_name}</span>
                      <span style={{ color: "#B37200" }}>+{formatComma(price)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={STYLES.muted}>ไม่มีรายการที่ต้องคิดแยกเพิ่ม</div>
              )}

              <div style={{ ...STYLES.row, borderTop: "1px dashed #D5DDE2", marginTop: "0.75rem", paddingTop: "0.75rem" }}>
                <span style={STYLES.muted}>
                  แพ็กเกจ {formatComma(plan.packageTotal)}
                  {plan.extraTotal > 0 && (
                    <span style={{ color: "#B37200", fontWeight: "bold" }}>
                      {" "}
                      + คิดแยกเพิ่ม {formatComma(plan.extraTotal)}
                    </span>
                  )}
                </span>
                <span>
                  รวมจ่ายจริง{" "}
                  <span style={{ fontSize: "1.4rem", fontWeight: "bold" }}>{formatComma(plan.total)}</span>
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ ...STYLES.muted, textAlign: "center", padding: "1rem" }}>
            ไม่พบแพ็กเกจที่ราคาถูกกว่ารายการที่เลือก
          </div>
        )}

        <div
          style={{
            ...STYLES.option,
            ...STYLES.row,
            marginTop: "1rem",
            ...(option === "NONE" && STYLES.optionActive),
          }}
          onClick={() => setOption("NONE")}
        >
          <Radio checked={option === "NONE"} label="ไม่ใช้แพ็กเกจ (คิดแยกรายการ)" style={{ fontWeight: "bold" }} />
          <span style={{ fontWeight: "bold" }}>{formatComma(plan.selectedTotal)}</span>
        </div>
      </Modal.Content>

      <Modal.Actions>
        <Button basic onClick={props.onClose}>
          ยกเลิก
        </Button>
        <ButtonLoadCheck
          setProp={props.setProp}
          paramKey={props.paramKey}
          buttonLoadCheck={props.buttonLoadCheck?.[props.paramKey]}
          style={{ backgroundColor: "var(--primary-theme-color)", color: "white" }}
          title="ยืนยัน"
          onClick={handleConfirm}
        />
      </Modal.Actions>
    </Modal>
  );
};

ModOptimizePackage.displayName = "ModOptimizePackage";

export default React.memo(ModOptimizePackage);
